import { useEffect } from 'react'
import { db, type CrmInviteSync } from '../lib/db'
import { upsertPersonInviteEvent } from '../lib/personActivity'
import type { CrmInviteEventBroadcast } from '../lib/crm'

// Subscribe to CRM_INVITE_EVENT_SYNCED broadcasts from the extension and:
//   1. Mirror the idempotency row into Dexie so useLiveQuery subscribers in
//      this page notice (the SW writes to its own origin-scoped IDB which is
//      invisible to Dexie here).
//   2. Forward the event to Supabase via upsert_person_invite_event.
//
// Mount once at the app shell. Unsubscribes on unmount. Events received while
// the web app was closed are recoverable via the "Backfill invitation history"
// button in CrmSettings — the SW's crmInviteSync holds the authoritative log.
export function useInviteSync() {
  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      // Only trust messages from our own window on our own origin. This
      // handler writes to Dexie + Supabase — an iframe spoofing the event
      // could plant bogus invite rows.
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'CRM_INVITE_EVENT_SYNCED') return
      const payload = event.data.event as CrmInviteEventBroadcast | undefined
      if (!payload?.profileUrn || !payload?.eventType) return

      // Mirror to Dexie — so any useLiveQuery subscribers tracking invite
      // state (future UI badges, analytics panels) notice immediately.
      try {
        const row: CrmInviteSync = {
          profileUrn: payload.profileUrn,
          eventType: payload.eventType,
          crmName: payload.crmName,
          affinityNoteId: payload.affinityNoteId,
          syncedAt: payload.syncedAt,
          eventAt: payload.eventAt,
        }
        await db.crmInviteSync.put(row)
      } catch (e) {
        console.warn('[CRM Invites] Dexie mirror failed:', e)
      }

      // Forward to Supabase. Non-fatal on failure — the Affinity write
      // already succeeded; backfill catches the miss later.
      try {
        await upsertPersonInviteEvent({
          profileUrn: payload.profileUrn,
          displayName: payload.counterpartyName,
          linkedinSlug: payload.linkedinSlug,
          eventType: payload.eventType,
          eventAt: payload.eventAt,
          counterpartyName: payload.counterpartyName,
        })
        console.log(
          '[CRM Invites] Upserted',
          payload.eventType,
          'for',
          payload.profileUrn,
        )
      } catch (e) {
        console.warn('[CRM Invites] Supabase upsert failed:', e)
      }
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])
}
